import React, { useState, useEffect } from 'react';
import { X, Check, Search } from 'lucide-react';
import { Client } from '../types';

interface CopyExercisesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (clientIds: number[]) => void;
  clients: Client[];
  excludeClientId: number;
  isLoading?: boolean;
}

const CopyExercisesModal: React.FC<CopyExercisesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  clients,
  excludeClientId,
  isLoading = false,
}) => {
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedClientIds([]);
      setClientSearchTerm('');
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  const filteredClients = clients.filter(
    (client) =>
      client.id !== excludeClientId &&
      (
        client.first_name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
        client.last_name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
        (client.email?.toLowerCase().includes(clientSearchTerm.toLowerCase()) ?? false)
      )
  );

  const selectedClients = clients.filter((client) => selectedClientIds.includes(client.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Копировать упражнения клиентам</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            onSubmit(selectedClientIds);
          }}
          className="p-6"
        >
          <div className="space-y-6">
            {/* Multi-select clients */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Выберите клиентов</label>
              <div className="relative">
                {selectedClients.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {selectedClients.map((client) => (
                      <span
                        key={client.id}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {client.first_name} {client.last_name}
                        <button
                          type="button"
                          onClick={() => setSelectedClientIds((prev) => prev.filter((id) => id !== client.id))}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <div className="flex items-center justify-between">
                      <span className={selectedClients.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedClients.length > 0
                          ? `Выбрано клиентов: ${selectedClients.length}`
                          : 'Выберите клиентов'}
                      </span>
                      <Check size={16} className="text-gray-400" />
                    </div>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Поиск клиентов..."
                            value={clientSearchTerm}
                            onChange={(e) => setClientSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredClients.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">Клиенты не найдены</div>
                        ) : (
                          filteredClients.map((client) => (
                            <button
                              key={client.id}
                              type="button"
                              onClick={() => setSelectedClientIds((prev) =>
                                prev.includes(client.id)
                                  ? prev.filter((id) => id !== client.id)
                                  : [...prev, client.id]
                              )}
                              className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                                selectedClientIds.includes(client.id) ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {client.first_name} {client.last_name}
                                </div>
                                <div className="text-sm text-gray-500">{client.email}</div>
                              </div>
                              {selectedClientIds.includes(client.id) && <Check size={16} className="text-blue-600" />}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {selectedClientIds.length === 0 && (
                <p className="mt-1 text-sm text-red-600">Выберите хотя бы одного клиента</p>
              )}
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={selectedClientIds.length === 0 || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Копирование...</span>
                </>
              ) : (
                <span>Копировать</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CopyExercisesModal; 